import { Routes, Route, Outlet } from 'react-router-dom'
import { Layout } from './components/AppLayout'
import { HomePage } from './pages/HomePage'
import { LoginPage } from './pages/LoginPage'
import { RegisterPage } from './pages/RegisterPage'
import { ResetPasswordPage } from './pages/ResetPasswordPage'
import { PhotosPage } from './pages/PhotosPage'
import { PhotoDetailPage } from './pages/PhotoDetailPage'
import { CollectionsPage } from './pages/CollectionsPage'
import { CollectionEditorPage } from './pages/CollectionEditorPage'
import { CollectionDetailPage } from './pages/CollectionDetailPage'
import { CollectionCritiqueModePage } from './pages/CollectionCritiqueModePage'
import { AdminPanel } from './pages/AdminPanel'
import { AdminUsersPage } from './pages/admin/AdminUsersPage'
import { AdminUserDetailPage } from './pages/admin/AdminUserDetailPage'
import { AdminUserRelationshipsPage } from './pages/admin/AdminUserRelationshipsPage'
import { AdminCreateTestUserPage } from './pages/admin/AdminCreateTestUserPage'
import { AdminCreateUserPage } from './pages/admin/AdminCreateUserPage'
import { AdminQueryConsolePage } from './pages/admin/AdminQueryConsolePage'
import { AdminAIPersonasPage } from './pages/admin/AdminAIPersonasPage'
import { AdminPersonaDetailPage } from './pages/admin/AdminPersonaDetailPage'
import { AdminCreatePersonaPage } from './pages/admin/AdminCreatePersonaPage'
import { AdminInvitesPage } from './pages/admin/AdminInvitesPage'
import { JobQueueInspectorPage } from './components/admin-job-queue/JobQueueInspectorPage'
import { AdminRoute } from './components/AdminRoute'
import { DevelopmentRoute } from './components/DevelopmentRoute'
import { ProtectedRoute } from './components/ProtectedRoute'
import { ObjectInspectorPage } from './pages/ObjectInspectorPage'
import { CritiqueFeedPage } from './pages/CritiqueFeedPage'
import { RecentCritiquesPage } from './pages/RecentCritiquesPage'
import { ContentSection } from './pages/ContentSection'
import VerifyEmailPage from './pages/VerifyEmailPage'
import { PrivacyPage } from './pages/PrivacyPage'
import { AboutPage } from './pages/AboutPage'
import { UserProfileRedirect } from './pages/UserProfileRedirect'
import { UserProfilePage } from './pages/UserProfilePage'
import { PhotoCollection2TestPage } from './pages/PhotoCollection2TestPage'
import { MarkupTestPage } from './pages/MarkupTestPage'
import { DevNotificationsPage } from './pages/DevNotificationsPage'
import { DevPhotoInspectorPage } from './pages/DevPhotoInspectorPage'
import { ImpersonatePage } from './pages/ImpersonatePage'
import { LoginLinkPage } from './pages/LoginLinkPage'
import { ShareLandingPage } from './pages/ShareLandingPage'
import { TasksPage } from './pages/TasksPage'
import LoggingConfig from './pages/DevTools/LoggingConfig'
import { DevFlagsProvider } from './contexts/DevFlagsContext'
import { ViewModeProvider } from './contexts/ViewModeContext'
import { CommandBusProvider, KeyboardCommandHandler } from './utils/commands'
import { LoggingProvider } from './contexts/LoggingContext'
import { UploadProvider } from './contexts/UploadContext'
import { Toaster } from 'sonner'

// Runtime validation for admin routes (development only)
// Ensures all /admin/* routes are properly nested and protected
// Note: Validation is disabled because it requires runtime route introspection
// which is complex with React Router v6. Route protection is enforced by:
// 1. Nested AdminRoute guard in route structure
// 2. Code review verification during development

function App() {
  return (
    <DevFlagsProvider>
      <ViewModeProvider>
        <CommandBusProvider>
          <LoggingProvider>
            <UploadProvider>
              <KeyboardCommandHandler />
              <Toaster position="top-right" richColors closeButton />
              <Routes>
              <Route path="/" element={<Layout />}>
                {/* Protected content - requires authentication (Feature: 647-invite-registration US4) */}
                <Route index element={<ProtectedRoute><HomePage /></ProtectedRoute>} />

                {/* Public auth routes - no authentication required */}
                <Route path="login" element={<LoginPage />} />
                <Route path="register" element={<RegisterPage />} />
                <Route path="reset-password" element={<ResetPasswordPage />} />
                <Route path="verify-email" element={<VerifyEmailPage />} />
                <Route path="auth/impersonate" element={<ImpersonatePage />} />
                <Route path="auth/login-link" element={<LoginLinkPage />} />
                {/* Share Landing Page - Feature 080 critique sharing */}
                <Route path="share/:token" element={<ShareLandingPage />} />
                <Route path="privacy" element={<PrivacyPage />} />
                <Route path="about" element={<AboutPage />} />

                {/* Admin Routes - ALL protected by nested AdminRoute guard (FR-016) */}
                <Route path="admin" element={<AdminRoute><Outlet /></AdminRoute>}>
                  <Route path="health" element={<AdminPanel />} />
                  <Route path="query-console" element={<AdminQueryConsolePage />} />
                  <Route path="obj" element={<ObjectInspectorPage />} />
                  <Route path="obj/:globalId" element={<ObjectInspectorPage />} />
                  <Route path="users" element={<AdminUsersPage />} />
                  <Route path="users/create" element={<AdminCreateUserPage />} />
                  <Route path="users/create-test" element={<AdminCreateTestUserPage />} />
                  <Route path="users/:globalId" element={<AdminUserDetailPage />} />
                  <Route path="users/:globalId/relationships" element={<AdminUserRelationshipsPage />} />
                  {/* AI Personas Management - Feature 071 */}
                  <Route path="ai-personas" element={<AdminAIPersonasPage />} />
                  <Route path="ai-personas/create" element={<AdminCreatePersonaPage />} />
                  <Route path="ai-personas/:globalId" element={<AdminPersonaDetailPage />} />
                  <Route path="ai-personas/:globalId/edit" element={<AdminPersonaDetailPage />} />
                  {/* Job Queue Inspector - Feature 073 */}
                  <Route path="job-queue" element={<JobQueueInspectorPage />} />
                  {/* Invite Management - Feature 647 */}
                  <Route path="invites" element={<AdminInvitesPage />} />
                </Route>

                {/* Development Routes - Available to all users in development mode */}
                <Route path="dev" element={<DevelopmentRoute><Outlet /></DevelopmentRoute>}>
                  <Route path="test/photo-collection2" element={<PhotoCollection2TestPage />} />
                  <Route path="test/markup" element={<MarkupTestPage />} />
                  <Route path="test/notifications" element={<DevNotificationsPage />} />
                  <Route path="tool/photos" element={<DevPhotoInspectorPage />} />
                  <Route path="tool/photos/:photoId" element={<DevPhotoInspectorPage />} />
                  <Route path="tools/logging" element={<LoggingConfig />} />
                </Route>

                {/* Static content pages - public */}
                <Route path="content/:slug" element={<ContentSection />} />

                {/* Protected: Collection routes (global ID-based) - Feature 647 US4 */}
                <Route path="c/:collectionId" element={<ProtectedRoute><CollectionDetailPage /></ProtectedRoute>} />
                <Route path="c/:collectionId/:slug" element={<ProtectedRoute><CollectionDetailPage /></ProtectedRoute>} />
                <Route path="c/:collectionId/:slug/edit" element={<ProtectedRoute><CollectionEditorPage /></ProtectedRoute>} />

                {/* Protected: Photo routes (global ID-based) - Feature 647 US4 */}
                <Route path="p/:photoId" element={<ProtectedRoute><PhotoDetailPage /></ProtectedRoute>} />

                {/* Protected: Tasks route - Feature 078 */}
                <Route path="tasks" element={<ProtectedRoute><TasksPage /></ProtectedRoute>} />

                {/* Protected: Discussion routes (formerly Critique) - Feature 890 */}
                <Route path="recent-discussions" element={<ProtectedRoute><RecentCritiquesPage /></ProtectedRoute>} />
                <Route path="disc" element={<ProtectedRoute><CritiqueFeedPage /></ProtectedRoute>} />
                <Route path="disc/:id" element={<ProtectedRoute><CollectionCritiqueModePage /></ProtectedRoute>} />
                <Route path="disc/:id/:slug" element={<ProtectedRoute><CollectionCritiqueModePage /></ProtectedRoute>} />

                {/* Legacy routes: Keep for backwards compatibility - Feature 890 */}
                <Route path="recent-critiques" element={<ProtectedRoute><RecentCritiquesPage /></ProtectedRoute>} />
                <Route path="crit" element={<ProtectedRoute><CritiqueFeedPage /></ProtectedRoute>} />
                <Route path="crit/:id" element={<ProtectedRoute><CollectionCritiqueModePage /></ProtectedRoute>} />
                <Route path="crit/:id/:slug" element={<ProtectedRoute><CollectionCritiqueModePage /></ProtectedRoute>} />

                {/* Protected: User-scoped routes - Feature 647 US4 */}
                <Route path="u" element={<ProtectedRoute><UserProfileRedirect /></ProtectedRoute>} />
                <Route path="u/:username" element={<ProtectedRoute><UserProfilePage /></ProtectedRoute>} />
                <Route path="u/:username/photos" element={<ProtectedRoute><PhotosPage /></ProtectedRoute>} />
                <Route path="u/:username/photos/:photoId" element={<ProtectedRoute><PhotoDetailPage /></ProtectedRoute>} />
                <Route path="u/:username/collections" element={<ProtectedRoute><CollectionsPage /></ProtectedRoute>} />

                {/* Protected: OLD ROUTES - Keep temporarily for backwards compatibility */}
                <Route path=":username/:slug/critique" element={<ProtectedRoute><CollectionCritiqueModePage /></ProtectedRoute>} />
              </Route>
              </Routes>
            </UploadProvider>
          </LoggingProvider>
        </CommandBusProvider>
      </ViewModeProvider>
    </DevFlagsProvider>
  )
}

export default App
